import React, { useState, useEffect } from "react";
import copy from "clipboard-copy";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "../../Style/CrmDashBoardTable.css";
import OutsideClickHandler from "react-outside-click-handler";
import DataTable from "react-data-table-component";
import { FaConciergeBell, FaSearch, FaPlus, FaCopy, FaFileCsv, FaFileExcel, FaFilePdf, FaPrint, FaColumns } from "react-icons/fa";

function Service({ tableData = [], onCreateClick }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(4);
  const [value, setValue] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  useEffect(() => {
    if (tableData?.length) {
      setItemsPerPage(4);
    }
  }, [tableData]);

  const handleSort = (column) => {
    setSortColumn(column);
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
  };

  const columns = [
    {
      name: <strong>Subject / Title</strong>,
      selector: (row) => row.subject || row.title || "-",
      sortable: true,
      cell: (row) => <span className="font-weight-bold text-dark">{row.subject || row.title || "-"}</span>,
    },
    {
      name: <strong>Type</strong>,
      selector: (row) => row.serviceRequestType || row.type || "Service",
      sortable: true,
      cell: (row) => <span>{row.serviceRequestType || row.type || "Service"}</span>,
    },
    {
      name: <strong>Status</strong>,
      selector: (row) => row.status || "-",
      sortable: true,
      cell: (row) => (
        <span
          style={{
            color: "#fff",
            backgroundColor:
              row.status === "Completed" || row.status === "Closed"
                ? "#28a745"
                : row.status === "InProgress"
                ? "#17a2b8"
                : "#ffc107",
            padding: "4px 8px",
            fontSize: "75%",
            fontWeight: "700",
            borderRadius: "0.25rem",
          }}
        >
          {row.status || "Open"}
        </span>
      ),
    },
    { name: <strong>Due Date</strong>, selector: (row) => row.duedate || row.dueDate || "-", sortable: true },
    { name: <strong>Priority</strong>, selector: (row) => row.priority || "-", sortable: true },
    { name: <strong>Assigned</strong>, selector: (row) => row.assigned || row.assignedto || "-", sortable: true },
    { name: <strong>Created</strong>, selector: (row) => row.createdBy || row.createdby || "-", sortable: true },
  ];

  const filteredData = (tableData || [])
    .filter((ele) =>
      value === ""
        ? true
        : Object.values(ele).some((val) =>
            val?.toString().toLowerCase().includes(value.toLowerCase())
          )
    )
    .sort((a, b) => {
      if (!sortColumn) return 0;
      const aValue = a?.[sortColumn] ?? "";
      const bValue = b?.[sortColumn] ?? "";
      return aValue > bValue
        ? sortDirection === "asc"
          ? 1
          : -1
        : aValue < bValue
        ? sortDirection === "asc"
          ? -1
          : 1
        : 0;
    });

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, filteredData.length);
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const [copied, setCopied] = useState(false);

  const handleCopyClick = () => {
    const formattedData = JSON.stringify(tableData, null, 2);
    copy(formattedData);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const convertToJson = () => {
    const csv = Papa.unparse(tableData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `service_requests.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const convertToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(tableData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, `service_requests.xlsx`);
  };

  const convertToPdf = () => {
    const doc = new jsPDF();
    const jsonString = JSON.stringify(tableData, null, 2);
    doc.text(jsonString, 10, 10);
    doc.save(`service_requests.pdf`);
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
        <html>
        <head><title>Print Service Requests</title></head>
        <body><pre>${JSON.stringify(tableData, null, 2)}</pre></body>
        </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const [visibile, setVisible] = useState(false);
  const handleDrop = () => setVisible(!visibile);

  return (
    <>
      {/* ── Toolbar + Search row ── */}
      <div
        className="d-flex justify-content-between align-items-center flex-wrap px-3 pt-3 pb-2"
        style={{ gap: "10px" }}
      >
        {/* Export buttons */}
        <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
          <button
            className="btn btn-outline-secondary btn-sm"
            style={{
              fontSize: "0.8rem",
              fontWeight: 500,
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
            }}
            onClick={handleCopyClick}
            title="Copy data"
          >
            <FaCopy />
            {copied ? "Copied!" : "Copy"}
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            style={{
              fontSize: "0.8rem",
              fontWeight: 500,
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
            }}
            onClick={convertToJson}
            title="Export CSV"
          >
            <FaFileCsv style={{ color: "#2e7d32" }} />
            CSV
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            style={{
              fontSize: "0.8rem",
              fontWeight: 500,
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
            }}
            onClick={convertToExcel}
            title="Export Excel"
          >
            <FaFileExcel style={{ color: "#1565c0" }} />
            Excel
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            style={{
              fontSize: "0.8rem",
              fontWeight: 500,
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
            }}
            onClick={convertToPdf}
            title="Export PDF"
          >
            <FaFilePdf style={{ color: "#c62828" }} />
            PDF
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            style={{
              fontSize: "0.8rem",
              fontWeight: 500,
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
            }}
            onClick={handlePrint}
            title="Print"
          >
            <FaPrint />
            Print
          </button>
          <OutsideClickHandler onOutsideClick={() => setVisible(false)}>
            <div className="dropdown position-relative">
              <button
                className="btn btn-outline-secondary btn-sm dropdown-toggle"
                style={{
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                }}
                onClick={handleDrop}
              >
                <FaColumns />
                Columns
              </button>
              <div
                className={`dropdown-menu ${visibile ? "show" : ""} p-2`}
                style={{ minWidth: "160px", right: 0 }}
              >
                {[
                  "Subject",
                  "Type",
                  "Status",
                  "Due Date",
                  "Priority",
                  "Assigned",
                  "Created",
                ].map((col, idx) => (
                  <div
                    key={idx}
                    className="dropdown-item rounded my-1 text-center py-1"
                    style={{
                      fontSize: "0.82rem",
                      backgroundColor: "#22c55e",
                      color: "#fff",
                      cursor: "default",
                    }}
                  >
                    {col}
                  </div>
                ))}
              </div>
            </div>
          </OutsideClickHandler>
        </div>

        {/* Search box */}
        <div className="d-flex align-items-center" style={{ gap: "8px" }}>
          <span className="text-muted" style={{ fontSize: "0.85rem", fontWeight: 500 }}>
            Search:
          </span>
          <div className="input-group" style={{ width: "280px", flexWrap: "nowrap" }}>
            <span
              className="input-group-text"
              style={{
                background: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRight: "none",
                borderRadius: "8px 0 0 8px",
                padding: "0 10px",
              }}
            >
              <FaSearch className="text-secondary" style={{ fontSize: "0.8rem" }} />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search requests..."
              onChange={(e) => setValue(e.target.value)}
              value={value}
              style={{
                border: "1px solid #dee2e6",
                borderLeft: "none",
                borderRadius: "0 8px 8px 0",
                fontSize: "0.875rem",
                height: "34px",
                transition: "box-shadow 0.2s",
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = "0 0 0 3px rgba(34,197,94,0.18)";
                e.target.style.outline = "none";
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = "none";
              }}
            />
          </div>
        </div>
      </div>

      {filteredData.length === 0 ? (
        <div className="text-center py-5 bg-light rounded border">
          <FaConciergeBell style={{ fontSize: "3rem", color: "#17a2b8", marginBottom: "1rem" }} />
          <h5 className="text-secondary">No Service Requests Found</h5>
          <p className="text-muted mb-3">There are no service requests logged for this lead yet.</p>
          {onCreateClick && (
            <button className="btn btn-info text-white btn-sm px-3 rounded-pill" onClick={onCreateClick}>
              <FaPlus className="mr-1" /> Create Service Request
            </button>
          )}
        </div>
      ) : (
        <div className="table-container border rounded">
          <DataTable
            columns={columns}
            data={paginatedData}
            pagination
            paginationServer
            paginationTotalRows={filteredData.length}
            onChangePage={setCurrentPage}
            onChangeRowsPerPage={(newPerPage) => {
              setItemsPerPage(newPerPage);
              setCurrentPage(1);
            }}
            sortServer
            onSort={(column) => handleSort(column.name)}
            highlightOnHover
            striped
          />
        </div>
      )}
    </>
  );
}

export default Service;
